import {
  Event,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
} from "@angular/router";
import {
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  Observable,
  OperatorFunction,
  pipe,
} from "rxjs";
import { isExistent } from "./miscellaneous";

/**
 * Gets the first existent (defined, non-null) value from an observable as a Promise.
 * @param observable The observable to pull the first value from.
 */
export function firstExistentValueFrom<T>(
  observable: Observable<T | null | undefined>,
): Promise<T> {
  return firstValueFrom(observable.pipe(filter(isExistent)));
}

/**
 * Maps a stream of router events to a flag indicating whether a route
 * is under way.
 */
export function isNavigationStateChange(): OperatorFunction<Event, boolean> {
  return pipe(
    map((event) => {
      if (event instanceof NavigationStart) {
        return true;
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        return false;
      }
      return null;
    }),
    filter(isExistent),
  );
}

/**
 * Maps the emissions from source **if** the source doesn't emit `null`.
 * Otherwise, the `null` value is passed through unchanged.
 *
 * @param project - The projection function to apply to each emission.
 */
export function mapNullable<T, U>(
  project: (value: T, index: number) => U,
): OperatorFunction<T | null, U | null> {
  return map((value, index) => (value === null ? null : project(value, index)));
}

/**
 * Partitions an observable into two separate streams based on a predicate filter.
 * @param observable The observable to partition the stream of.
 * @param predicate Predicate for determining how to split the results.
 * `true` return values go to the first result observable, `false` to the second.
 */
export function partition<T, R extends T>(
  observable: Observable<T>,
  predicate: (value: T) => value is R,
): [Observable<R>, Observable<Exclude<T, R>>];
export function partition<T>(
  observable: Observable<T>,
  predicate: (value: T) => boolean,
): [Observable<T>, Observable<T>];
export function partition<T, R extends T = T>(
  observable: Observable<T>,
  predicate: (value: T) => boolean,
): [Observable<R>, Observable<Exclude<T, R>>] {
  const success = observable.pipe(
    filter((value): value is R => predicate(value)),
  );
  const failure = observable.pipe(
    filter((value): value is Exclude<T, R> => !predicate(value)),
  );
  return [success, failure];
}

export function pluckDistinctUntilChanged<T, K extends keyof T>(
  key: K,
  comparator?: (previous: T[K] | null, current: T[K] | null) => boolean,
): OperatorFunction<T, T[K]>;
export function pluckDistinctUntilChanged<T, K extends keyof T>(
  key: K,
  comparator?: (previous: T[K] | null, current: T[K] | null) => boolean,
): OperatorFunction<T | null | undefined, T[K] | null>;
export function pluckDistinctUntilChanged<T, K extends keyof T>(
  key: K,
  comparator?: (previous: T[K] | null, current: T[K] | null) => boolean,
): OperatorFunction<T | null | undefined, T[K] | null> {
  return pipe(
    map((obj) => obj?.[key] ?? null),
    distinctUntilChanged(comparator),
  );
}
